import React from 'react';
import { Link } from 'react-router-dom';
import { Plus, Minus, Sparkles, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const FAQ = () => {
  const faqs = [
    {
      question: '¿Necesito tener experiencia previa en temas de consciencia o espiritualidad?',
      answer: 'No, en absoluto. Cada formación está diseñada para acompañarte desde cero. No necesitas saber nada especial ni creer en nada nuevo. Solo estar aquí, tal como estás.'
    },
    {
      question: '¿Cómo funcionan las formaciones online?',
      answer: 'Las formaciones incluyen contenido en vídeo, audio y material escrito que puedes revisar a tu ritmo. Cada módulo está estructurado para que puedas integrar los aprendizajes de forma gradual y consciente.'
    },
    {
      question: '¿Cuánto tiempo tengo para completar un curso?',
      answer: 'Una vez inscrito, tienes acceso de por vida al contenido del curso. Puedes avanzar a tu propio ritmo, sin presiones ni fechas límite. Tu proceso es único y lo respetamos.'
    },
    {
      question: '¿Qué son los Registros Akáshicos?',
      answer: 'Los Registros Akáshicos son un campo de información que contiene el registro de cada alma y su viaje. En las formaciones aprenderás a conectar con esta fuente de sabiduría de forma práctica, clara y sin misticismos innecesarios.'
    },
    {
      question: '¿Ofreces sesiones individuales?',
      answer: 'Actualmente no ofrezco sesiones individuales a través de la plataforma. Mi enfoque está en las formaciones grupales, donde puedo acompañar a más personas en su proceso de autoconocimiento y sanación.'
    },
    {
      question: '¿Qué métodos de pago aceptáis?',
      answer: 'Aceptamos pagos a través de PayPal, que permite pagar con tu cuenta PayPal o directamente con tarjeta de crédito/débito sin necesidad de cuenta. Es un proceso seguro y sencillo.'
    },
    {
      question: '¿Cómo puedo contactarte si tengo dudas?',
      answer: 'Puedes escribirme a través del formulario de contacto en la web. Respondo personalmente a todos los mensajes, aunque puede llevarme unos días dependiendo del volumen.'
    },
    {
      question: '¿Las formaciones son solo teóricas o incluyen práctica?',
      answer: 'Todas las formaciones combinan teoría y práctica. Creo firmemente que el verdadero aprendizaje viene de la experiencia, por eso cada módulo incluye ejercicios prácticos que puedes aplicar en tu vida cotidiana.'
    },
    {
      question: '¿Necesito algún material especial para los cursos?',
      answer: 'No necesitas nada especial. Un espacio tranquilo donde puedas ver los vídeos y practicar es suficiente. Algunos ejercicios pueden requerir un cuaderno para tomar notas, pero nada más.'
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-white to-[#faf8f5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#c9a227]/10 rounded-full px-4 py-2 mb-6">
              <Sparkles className="w-4 h-4 text-[#c9a227]" />
              <span className="text-sm text-[#c9a227] font-medium">Escuela de Consciencia Akáshica</span>
            </div>
            
            <h1 
              className="text-3xl sm:text-4xl lg:text-5xl font-heading text-[hsl(var(--text-primary))] mb-6 leading-tight"
              data-testid="faq-title"
            >
              Preguntas frecuentes
            </h1>
            <p className="text-lg text-[hsl(var(--text-secondary))] leading-relaxed max-w-2xl mx-auto">
              Respuestas claras a las dudas más comunes. Si no encuentras lo que buscas, 
              no dudes en contactarme.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <div className="w-12 h-[2px] bg-[#c9a227] mx-auto mb-6" />
            <h2 className="text-2xl sm:text-3xl font-heading text-[hsl(var(--text-primary))]">
              Dudas resueltas
            </h2>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="card-soft border-none"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left hover:no-underline px-6 py-5 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block">
                  <span className="font-heading text-lg text-[hsl(var(--text-primary))] pr-4">
                    {faq.question}
                  </span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                    <Minus className="w-5 h-5 text-[hsl(var(--accent))] hidden" strokeWidth={1.5} />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-5 pt-0">
                  <p className="text-[hsl(var(--text-secondary))] leading-relaxed">
                    {faq.answer}
                  </p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-[#faf8f5]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <div className="w-16 h-16 rounded-full bg-[#c9a227]/10 flex items-center justify-center mx-auto mb-6">
            <MessageCircle className="w-8 h-8 text-[#c9a227]" strokeWidth={1.5} />
          </div>
          <h2 className="text-2xl sm:text-3xl font-heading text-[hsl(var(--text-primary))] mb-4">
            ¿Tienes más preguntas?
          </h2>
          <p className="text-[hsl(var(--text-secondary))] mb-8">
            Escríbeme y te responderé personalmente.
          </p>
          <Link to="/contacto" data-testid="faq-contacto">
            <Button className="bg-[#c9a227] hover:bg-[#b89220] text-white px-8 py-6 rounded-full font-medium shadow-lg hover:shadow-xl transition-all">
              Contactar
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
